"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, PenTool, Cog, Factory } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
const steps = [{
  title: "Market Intelligence Audit",
  description: "We deploy deep-dive analytics to dissect your current market position, competitor maneuvers, and untapped audience segments for a data-driven foundation.",
  icon: MessageSquare
}, {
  title: "Strategic Architecture",
  description: "Our consultants engineer a bespoke marketing blueprint, aligning high-level brand positioning with aggressive growth objectives and media planning.",
  icon: PenTool
}, {
  title: "Creative Stress-Testing",
  description: "Before deployment, we subject creative assets and messaging to rigorous multivariate testing to ensure maximum resonance and conversion efficiency.",
  icon: Cog
}, {
  title: "Precision Execution",
  description: "Leveraging elite media partnerships, we launch your campaign with surgical precision, providing real-time optimization and transparent performance scaling.",
  icon: Factory
}];
export default function CooperationProcess() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height - windowHeight;
      const scrolledDistance = -rect.top;
      if (totalScrollableDistance > 0) {
        let percentage = scrolledDistance / totalScrollableDistance * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        setProgress(percentage);
      } else {
        if (rect.top <= 0) {
          setProgress(100);
        } else {
          setProgress(0);
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return <section data-section-id="224221" id="the-benchmark-framework" ref={sectionRef} className="relative dark bg-background py-16 md:py-32">
      {}
      <div className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-xl border-b border-white/10 shadow-2xl py-4 mb-12">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl flex items-center gap-4">
          <span className="text-xs font-bold font-mono text-primary whitespace-nowrap uppercase tracking-widest">
            Framework Phase
          </span>
          <Progress value={progress} className="h-1.5 flex-1 bg-white/10" />
          <span className="text-xs font-bold text-foreground font-mono tabular-nums w-12 text-right">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-7xl font-black font-default text-foreground mb-4 tracking-tighter uppercase">
            The Benchmark Framework
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Our proprietary architectural approach to scaling brand authority and dominating market share through elite strategy.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-6 md:left-8 top-4 bottom-4 w-px bg-gradient-to-b from-primary via-primary/20 to-transparent rounded-full hidden sm:block" />

          <div className="space-y-12 md:space-y-24">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative flex flex-col sm:flex-row items-start gap-6 md:gap-12 group">
                  {}
                  <div className="relative z-10 flex-shrink-0 flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-sm bg-background border border-white/10 group-hover:border-primary transition-all duration-500 shadow-lg sm:ml-0 ml-4 group-hover:shadow-primary/20">
                    <Icon className="w-5 h-5 md:w-7 md:h-7 text-muted-foreground group-hover:text-primary transition-colors duration-500" />
                  </div>

                  {}
                  <Card className="flex-1 w-full bg-white/5 border-white/10 backdrop-blur-sm shadow-none hover:bg-white/[0.08] transition-all duration-500 rounded-sm">
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-none font-mono uppercase tracking-widest border border-primary/20">
                          Deliverable 0{i + 1}
                        </span>
                      </div>
                      <CardTitle className="text-2xl md:text-3xl font-default font-bold text-foreground tracking-tight">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base text-muted-foreground font-default leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}